import { useEffect, useMemo, useRef, useState } from "react";
import apiPaths from "../../../api/endpoints";
import { stripSignedUrlQuery } from "../../Utilities/utilFunctions";
import useRemintAsset from "../../../hooks/useRemintAsset";
import { isAssetValid, AUDIO_BUFFER_SEC } from "../../../utils/assetExpiry";
import "./AudioPlayer.scss";

export interface AudioPlayerProps {
  /** Audio source — either a full URL or a relative `/api/getAudio/output/...` path */
  uri?: string;
  /** External stop command (parent-driven) — mirrors mobile AudioPlayer's stopAudioPlayer */
  stopAudio?: boolean;
  /** Try to auto-play when the src changes. Browsers may block without user gesture. */
  autoPlay?: boolean;
  /** Loop the audio (default: false) */
  loop?: boolean;
  /** Visual variant: 'pill' is full bar with waves; 'compact' is just a circular button */
  variant?: "pill" | "compact";
  /** Optional CSS class on the wrapper */
  className?: string;
  /** Fires when playback ends naturally */
  onEnded?: () => void;
  /** Playback speed multiplier (default: 1) */
  playbackRate?: number;
}

/**
 * Reusable web audio player — drop-in equivalent of the mobile AudioPlayer component.
 *
 * Handles:
 *   - Relative `/api/getAudio/output/...` paths → prefixes apiPaths.CONTENT_BASE_URL2
 *   - Auto-play attempt (gracefully falls back if browser blocks)
 *   - External stop command via `stopAudio` prop
 *   - Cleanup on unmount / src change
 *
 * Usage:
 *   <AudioPlayer uri={audioUrl} autoPlay />
 *   <AudioPlayer uri={audioUrl} variant="pill" />
 */
function AudioPlayer({
  uri,
  stopAudio,
  autoPlay = true,
  loop = false,
  variant = "compact",
  className = "",
  onEnded,
  playbackRate = 1,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  // Tracks the active (possibly reminted) URI for this player.
  const [activeUri, setActiveUri] = useState(uri);
  // Incremented when remint delivers a genuinely fresh token so the Audio
  // lifecycle effect reruns even though the content path (contentKey) is unchanged.
  const [tokenVersion, setTokenVersion] = useState(0);
  const { remint } = useRemintAsset();

  // On URI change: adopt immediately for content-key change detection, then
  // asynchronously remint if the token is expired/about to expire.  When remint
  // returns a different URL (fresh token), bump tokenVersion so the lifecycle
  // effect recreates the Audio element with the working URL.
  useEffect(() => {
    if (!uri) {
      setActiveUri(uri);
      return;
    }
    setActiveUri(uri);
    if (!isAssetValid(uri, AUDIO_BUFFER_SEC)) {
      remint([uri]).then(([fresh]) => {
        if (fresh && fresh !== uri) {
          setActiveUri(fresh);
          setTokenVersion((v) => v + 1);
        }
      });
    }
  }, [uri]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve relative API paths to full URLs (mirrors mobile logic)
  const resolvedUrl = useMemo(() => {
    if (!activeUri) return null;
    if (activeUri.includes("/api/getAudio/output")) {
      return `${apiPaths.CONTENT_BASE_URL}${activeUri}`;
    }
    return activeUri;
  }, [activeUri]);

  // Holds the latest resolvedUrl so the lifecycle effect always plays the freshest
  // URL — including the one just returned by remint — without that full URL being
  // a direct dep (which would cause needless recreation on every token refresh).
  const resolvedUrlRef = useRef(resolvedUrl);
  resolvedUrlRef.current = resolvedUrl;

  // Content key: derived from the *prop* URI so content changes are detected
  // immediately, independent of when remint resolves.
  const contentKey = uri
    ? stripSignedUrlQuery(
        uri.includes("/api/getAudio/output")
          ? `${apiPaths.CONTENT_BASE_URL}${uri}`
          : uri,
      )
    : null;

  // Manage Audio element lifecycle whenever the underlying content changes OR
  // when remint delivers a fresh token for a previously-expired URL (tokenVersion).
  useEffect(() => {
    const currentResolvedUrl = resolvedUrlRef.current;
    if (!currentResolvedUrl) {
      audioRef.current = null;
      setPlaying(false);
      return;
    }

    const audio = new Audio(currentResolvedUrl);
    audio.preload = "auto";
    audio.loop = loop;
    audio.playbackRate = playbackRate;
    audioRef.current = audio;

    const handleEnded = () => {
      setPlaying(false);
      onEnded?.();
    };
    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    if (autoPlay) {
      audio.play().catch(() => setPlaying(false));
    }

    return () => {
      audio.pause();
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audioRef.current = null;
    };
    // loop/autoPlay/onEnded are real dependencies — an unstable onEnded identity
    // across renders still recreates the Audio element even when contentKey hasn't changed.
  }, [contentKey, tokenVersion, loop, autoPlay, onEnded]);

  // Update playback rate on existing audio element without recreating it
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Parent-driven stop
  useEffect(() => {
    if (stopAudio && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [stopAudio]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  };

  if (!resolvedUrl) return null;
  return null;
  // return (
  //   <button
  //     type="button"
  //     className={`audio-player audio-player--${variant}${playing ? " audio-player--playing" : ""}${className ? ` ${className}` : ""}`}
  //     onClick={togglePlay}
  //     aria-label={playing ? "Pause audio" : "Play audio"}
  //   >
  //     {playing ? (
  //       <span className="audio-player__waves" aria-hidden="true">
  //         <span />
  //         <span />
  //         <span />
  //         <span />
  //       </span>
  //     ) : (
  //       <svg
  //         className="audio-player__icon"
  //         width="20"
  //         height="20"
  //         viewBox="0 0 24 24"
  //         fill="none"
  //         stroke="currentColor"
  //         strokeWidth="2"
  //         strokeLinecap="round"
  //         strokeLinejoin="round"
  //       >
  //         <polygon
  //           points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"
  //           fill="currentColor"
  //         />
  //         <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
  //         <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  //       </svg>
  //     )}
  //   </button>
  // );
}

export default AudioPlayer;
